/**
 * @fileoverview Drawer/modal state of the Task Tracker page: which assessment,
 * final-assessment or deliverable drawer is open and for whom, plus the
 * AR-924 "Upload for class" target. The deliverable drawer carries an
 * `openToken` that changes on every open — the page uses it as React `key`,
 * so the drawer remounts with fresh state (no reset-by-effect).
 */

import { useCallback, useState } from 'react'
import type { TaskTrackMatrix } from '../types'
import { buildUploadForClassTarget, type UploadForClassTarget } from '../uploadForClass'

export interface AssessmentDrawerState {
  opened: boolean
  sessionMeetingId: string
  studentId: string | null
}

export interface DeliverableDrawerState {
  opened: boolean
  openToken: number
  sessionMeetingActivityId: string
  studentId: string
  studentFullName: string
}

const CLOSED_ASSESSMENT: AssessmentDrawerState = { opened: false, sessionMeetingId: '', studentId: null }
const CLOSED_DELIVERABLE: DeliverableDrawerState = {
  opened: false,
  openToken: 0,
  sessionMeetingActivityId: '',
  studentId: '',
  studentFullName: '',
}

interface UseTaskTrackerDrawersArgs {
  matrix: TaskTrackMatrix | null
  projectId: string
  projectName: string
}

export function useTaskTrackerDrawers({ matrix, projectId, projectName }: UseTaskTrackerDrawersArgs) {
  const [assessment, setAssessment] = useState(CLOSED_ASSESSMENT)
  const [finalStudentId, setFinalStudentId] = useState<string | null>(null)
  const [deliverable, setDeliverable] = useState(CLOSED_DELIVERABLE)
  const [uploadTarget, setUploadTarget] = useState<UploadForClassTarget | null>(null)

  const openAssessment = useCallback((sessionMeetingId: string, studentId: string | null) => {
    setAssessment({ opened: true, sessionMeetingId, studentId })
  }, [])
  const closeAssessment = useCallback(() => setAssessment(CLOSED_ASSESSMENT), [])

  const openFinal = useCallback((studentId: string) => {
    if (projectId && studentId) setFinalStudentId(studentId)
  }, [projectId])
  const closeFinal = useCallback(() => setFinalStudentId(null), [])

  const openDeliverable = useCallback((activityId: string, studentId: string) => {
    const student = matrix?.students.find(s => s.id === studentId)
    if (!student) return
    setDeliverable(current => ({
      opened: true,
      openToken: current.openToken + 1,
      sessionMeetingActivityId: activityId,
      studentId,
      studentFullName: student.fullName,
    }))
  }, [matrix])
  const closeDeliverable = useCallback(() => setDeliverable(current => ({ ...current, opened: false })), [])

  // "Assess now" (AR-924): swap the deliverable drawer for the assessment drawer of the same lesson.
  const assessFromDeliverable = useCallback((lessonId: string) => {
    setAssessment({ opened: true, sessionMeetingId: lessonId, studentId: deliverable.studentId })
    setDeliverable(current => ({ ...current, opened: false }))
  }, [deliverable.studentId])

  const openUploadForClass = useCallback((activityId: string) => {
    if (!matrix) return
    setUploadTarget(buildUploadForClassTarget(matrix, activityId, projectName))
  }, [matrix, projectName])
  const closeUploadForClass = useCallback(() => setUploadTarget(null), [])

  return {
    assessment,
    openAssessment,
    closeAssessment,
    finalStudentId,
    openFinal,
    closeFinal,
    deliverable,
    openDeliverable,
    closeDeliverable,
    assessFromDeliverable,
    uploadTarget,
    openUploadForClass,
    closeUploadForClass,
  }
}
